import { Grid, Paper } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { Box } from '@mui/system';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { MemberStripeStatusSearchFilter } from '../models/memberStripeStatusSearchFilter';
import { MemberStripeStatusDataGrid } from '../components/memberStripeStatusDataGrid';
import { StripeTabNavigation } from './stripeTabNavigation';

// Kopplade medlemmar
export const MemberStripeStatusOverviewPage = () => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [memberStripeStatusFilter, setMemberStripeStatusFilter] = useLocalStorage(LocalStorageKeys.MemberStripeStatusFilter, {
    sortCol: 'nextInvoiceAfter',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as MemberStripeStatusSearchFilter);

  const searchFilter: MemberStripeStatusSearchFilter = useMemo(() => {
    return {
      filter: memberStripeStatusFilter?.filter,
      pageSize: memberStripeStatusFilter?.pageSize,
      pageIndex: memberStripeStatusFilter?.pageIndex,
      sortCol: memberStripeStatusFilter?.sortCol,
      sortDir: memberStripeStatusFilter?.sortDir,
    } as MemberStripeStatusSearchFilter;
  }, [
    memberStripeStatusFilter?.filter,
    memberStripeStatusFilter?.pageIndex,
    memberStripeStatusFilter?.pageSize,
    memberStripeStatusFilter?.sortCol,
    memberStripeStatusFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setMemberStripeStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setMemberStripeStatusFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setMemberStripeStatusFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setMemberStripeStatusFilter, tempSearchString]);

  return (
    <GridWrapper>
      <Grid
        item
        xs={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <MainContentHeader title={t('invoice:stripeStatus.header')} />
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <StripeTabNavigation />
          <PageFilterSection
            leftContent={
              <SearchField
                initialValue={searchFilter.filter || ''}
                onSearch={(newSearchString) => {
                  setSearchInitialized(true);
                  setTempSearchString(newSearchString);
                }}
              />
            }
          />
          <Box className='section-inset'>
            <MemberStripeStatusDataGrid
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setMemberStripeStatusFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
            />
          </Box>
        </Paper>
      </Grid>
    </GridWrapper>
  );
};
